import * as React from 'react'

/**
 * A custom hook that triggers a callback when a click or touch occurs outside of the passed ref.
 */
export const useOnClickOutside = <T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  callback: (event: MouseEvent | TouchEvent) => void
) => {
  React.useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      // Do nothing if the ref is not attached yet, or if the click is inside the ref's element.
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return
      }
      // Trigger the callback if the click is outside.
      callback(event)
    }

    // Listen for both mouse and touch events for better mobile support.
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)

    // The cleanup function to remove the listeners when the component unmounts.
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, callback])
}
